// src/shared/hooks/useDateTimeFormatter.ts
import { useMemo } from "react";
import { useUser } from "src/shared/context/UserContext";
import { DateFormat, TimeFormat } from "../types/configuration.types";
import { formatDate as formatDateUtil } from "src/shared/utils/dateFormatter";

export const useDateTimeFormatter = () => {
  const { userConfigration } = useUser();

  // Get the configured date format or use default
  const dateFormat = useMemo(() => {
    return (userConfigration?.dateFormat as DateFormat) || "DD/MM/YYYY";
  }, [userConfigration?.dateFormat]);

  // Get the configured time format or use default
  const timeFormat = useMemo(() => {
    return (userConfigration?.timeFormat as TimeFormat) || "12h";
  }, [userConfigration?.timeFormat]);

  // Format date with the configured format
  const formatDate = (date: Date | string): string => {
    if (!date) return "";
    return formatDateUtil(date, dateFormat);
  };

  // Format time with the configured format
  const formatTime = (time: Date | string): string => {
    if (!time) return "";

    try {
      let date: Date;

      // If time is a string
      if (typeof time === "string") {
        // Check if it's a full date string with time (e.g., "2026-08-04T09:58:04Z")
        if (time.includes("T")) {
          date = new Date(time);
        }
        // Check if it's just time (e.g., "09:58:04" or "09:58")
        else if (time.includes(":")) {
          const parts = time.split(":");
          if (parts.length >= 2) {
            const hours = parseInt(parts[0]);
            const minutes = parts[1];
            const seconds = parts[2] ?? "00";

            if (timeFormat === "12h") {
              const hour12 = hours % 12 || 12;
              const ampm = hours >= 12 ? "PM" : "AM";
              return `${hour12}:${minutes}:${seconds} ${ampm}`;
            } else {
              return `${hours.toString().padStart(2, "0")}:${minutes}:${seconds}`;
            }
          }
          return time;
        }
        // Try to parse as date
        else {
          date = new Date(time);
        }
      } else if (time instanceof Date) {
        date = time;
      } else {
        return String(time);
      }

      // If date is invalid, return the original
      if (isNaN(date.getTime())) {
        return String(time);
      }

      // Format the date object to time
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: timeFormat === "12h",
      });
    } catch (e) {
      return String(time);
    }
  };

  // Format date and time together
  const formatDateTime = (date: Date | string): string => {
    if (!date) return "";

    try {
      const d = typeof date === "string" ? new Date(date) : date;
      if (isNaN(d.getTime())) return String(date);

      const formattedDate = formatDate(d);
      const formattedTime = d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: timeFormat === "12h",
      });

      return `${formattedDate} ${formattedTime}`;
    } catch (e) {
      return String(date);
    }
  };

  return {
    formatDate,
    formatTime,
    formatDateTime,
    dateFormat,
    timeFormat,
  };
};
